import { AppConfiguration, useLeadStatuses } from "@/api/app-configuration.api";
import { EditorLockContext } from "@/services/editor-lock.service";
import { User } from "@/types/auth";
import {
  Lead,
  LeadDeliveryStatus,
  LeadDeliveryStatusEnum,
  NewCampaign,
  SimpleCampaign,
} from "@/types/campaign";
import { formatDate2, formatDateTime } from "@/utils/dates";
import { NavigateOptions } from "@tanstack/react-router";
import { CircleAlert, ClockIcon } from "lucide-react";
import { memo, ReactNode, useContext, useMemo, useRef } from "react";
import { FocusableOptions, useFocusable } from "react-aria";
import { SortDescriptor } from "react-stately";
import { twMerge } from "tailwind-merge";
import { Avatar } from "../generic/Avatar";
import { createTypedTable, ghostItem } from "../generic/Table";
import { Tooltip } from "../generic/Tooltip";

export type LeadsTableItem = {
  id: number;
  lead: Lead;
  name: string;
  editor: string;
  campaignName: string;
  configuration: string;
  created: string;
  followUpTime: string;
  status: string;
  statusLabel: string;
  statusReason: string;
  responsible: string;
  contact: string;
  deliveryStatus: LeadDeliveryStatus | null;
  deliveryError: string | null;
};

const typedTable = createTypedTable<LeadsTableItem>();

export const { TableHeader: LeadsTableHeader, TableBody: LeadsTableBody } =
  typedTable;

const leadTypeColors: Partial<Record<string, string>> = {
  Guld: "text-theme-gold",
  Sølv: "text-theme-silver",
  Bronze: "text-theme-bronze",
};

const Focusable = (
  props: React.JSX.IntrinsicElements["div"] & FocusableOptions<HTMLDivElement>,
) => {
  const ref = useRef<HTMLDivElement>(null);
  const { focusableProps } = useFocusable(props, ref);
  return (
    <div
      {...focusableProps}
      ref={ref}
      className={twMerge(
        focusableProps.className,
        "outline-0 outline-offset-2 outline-blue-500 focus-visible:outline-2",
        props.className,
      )}
    >
      {props.children}
    </div>
  );
};

const leadsTableColumns = typedTable.createColumns([
  {
    id: "name",
    name: "Navn",
    isRowHeader: true,
    width: 150,
    className: "min-w-[250px]",
    renderCell: (item) => (
      <div className="relative">
        {item.editor && (
          <Tooltip tooltipText={`${item.editor} kigger på dette lead`}>
            <Focusable className="absolute top-1/2 -left-7 hidden -translate-x-1/2 -translate-y-1/2 rounded-full xl:block">
              <Avatar size="xs" user={{ name: item.editor }} />
            </Focusable>
          </Tooltip>
        )}

        <div className="truncate">{item.name}</div>
      </div>
    ),
  },
  {
    id: "configuration",
    name: "Type",
    width: 30,
    renderCell: (item) => (
      <span className={leadTypeColors[item.configuration]}>
        {item.configuration}
      </span>
    ),
  },
  {
    id: "status",
    name: "Status",
    width: 120,
    renderCell: ({
      status,
      statusLabel,
      statusReason,
      deliveryError,
      deliveryStatus,
    }) =>
      deliveryStatus === LeadDeliveryStatusEnum.IntegrationFailed ? (
        <div className="flex items-center gap-x-2">
          <Tooltip
            className="pointer-events-none"
            tooltipTriggerProps={{
              delay: 0,
              closeDelay: 0,
              isDisabled: !deliveryError,
            }}
            tooltipText={
              deliveryError
                ? `Levering til integration fejlede med årsagen: ${deliveryError}`
                : ""
            }
          >
            <Focusable
              className="flex items-center gap-x-2 self-start rounded-sm outline-offset-8"
              isDisabled={!deliveryError}
            >
              Integrationsfejl
              <CircleAlert
                absoluteStrokeWidth
                className="size-3.5 shrink-0 grow-0"
              />
            </Focusable>
          </Tooltip>
        </div>
      ) : (
        status && (
          <Tooltip
            className="pointer-events-none"
            tooltipTriggerProps={{
              delay: 0,
              closeDelay: 0,
              isDisabled: !statusReason,
            }}
            tooltipText={statusReason}
          >
            <Focusable
              className="flex items-center gap-x-2 self-start rounded-sm outline-offset-8"
              isDisabled={!statusReason}
            >
              <span>{statusLabel}</span>
              {status === "FollowUpDue" && (
                <ClockIcon className="size-3.5 shrink-0 grow-0" />
              )}
            </Focusable>
          </Tooltip>
        )
      ),
  },
  {
    id: "campaignName",
    name: "Kampagne",
    width: 80,
  },
  {
    id: "responsible",
    name: "Ansvarlig",
    width: 80,
  },
  {
    id: "created",
    name: "Leveret",
    isSortable: true,
    defaultSortDirection: "descending",
    width: 60,
  },
  {
    id: "followUpTime",
    name: "Opfølgning",
    isSortable: true,
    defaultSortDirection: "ascending",
    width: 80,
  },
  {
    id: "contact",
    name: "Kontaktperson",
    width: 90,
  },
]);

export type LeadsTableColumn = (typeof leadsTableColumns)[number];

export const LeadsTable = memo(
  ({
    className,
    pickColumns,
    omitColumns,
    leads,
    campaigns,
    users,
    sortDescriptor,
    setSortDescriptor,
    buildLeadLink,
    onClickLead,
    children,
  }: (
    | {
        pickColumns?: Partial<Record<LeadsTableColumn["id"], boolean>>;
        omitColumns?: undefined;
      }
    | {
        pickColumns?: undefined;
        omitColumns?: Partial<Record<LeadsTableColumn["id"], boolean>>;
      }
  ) & {
    className?: string;
    leads?: (Lead | typeof ghostItem)[];
    campaigns?: NewCampaign[] | SimpleCampaign[];
    appConfig?: AppConfiguration;
    users: User[];
    sortDescriptor: SortDescriptor;
    setSortDescriptor: (sortDescriptor: SortDescriptor) => void;
    buildLeadLink?: (lead: Lead) => NavigateOptions;
    onClickLead?: (lead: Lead) => void;
    children?: ReactNode;
  }) => {
    const { getLeadStatusLabel } = useLeadStatuses();

    const desiredColumns = useMemo(() => {
      if (pickColumns) return pickColumns;
      const desiredColumns: Partial<Record<LeadsTableColumn["id"], boolean>> =
        {};
      for (const column of leadsTableColumns) {
        desiredColumns[column.id] = omitColumns?.[column.id] !== true;
      }
      return desiredColumns;
    }, [pickColumns, omitColumns]);

    const columns = useMemo(() => {
      let columns = leadsTableColumns;

      if (desiredColumns) {
        columns = columns.filter((column) => desiredColumns[column.id]);
      }

      return columns;
    }, [desiredColumns]);

    const userNamesById = useMemo(
      () => new Map(users.map((user) => [user.id, user.name])),
      [users],
    );

    const { campaignsById } = useMemo(() => {
      const campaignsById = new Map<
        number,
        {
          id: number;
          name: string;
          configurationNamesById: Map<number, string>;
          responsibleName: string;
        }
      >();

      for (const campaign of campaigns || []) {
        if (campaign.id === undefined) continue;

        const configurationNamesById = new Map<number, string>();
        for (const configuration of campaign.configurations) {
          if (
            configuration.id === undefined ||
            configuration.id === null ||
            !configuration.name
          )
            continue;
          configurationNamesById.set(configuration.id, configuration.name);
        }

        campaignsById.set(campaign.id, {
          id: campaign.id,
          name: campaign.name,
          configurationNamesById,
          responsibleName:
            (campaign.responsibleId
              ? userNamesById.get(campaign.responsibleId)
              : null) ??
            ("responsibleName" in campaign ? campaign.responsibleName : null) ??
            "",
        });
      }

      return { campaignsById };
    }, [campaigns, userNamesById]);

    const editorLockState = useContext(EditorLockContext);

    const items = useMemo(
      () =>
        (leads ?? []).map((lead): LeadsTableItem | typeof ghostItem => {
          if (lead === ghostItem) return ghostItem;
          const campaign = campaignsById.get(lead.campaignId);
          const createdDate = new Date(lead.created);
          const followUpDate = lead.followUpTime
            ? new Date(lead.followUpTime)
            : null;
          const editorUserId = editorLockState?.editorLocks.get(
            `sales/lead/${lead.id}`,
          )?.currentEditor?.userId;
          return {
            lead,
            id: lead.id,
            name: lead.name,
            editor: (editorUserId && userNamesById.get(editorUserId)) || "",
            campaignName: campaign?.name ?? "",
            configuration:
              campaign?.configurationNamesById.get(lead.configurationId) ?? "",
            created: formatDate2(createdDate),
            followUpTime: followUpDate
              ? formatDateTime(followUpDate, {
                  hour: "numeric",
                  minute: "numeric",
                  day: "2-digit",
                  month: "2-digit",
                })
              : "",
            deliveryError: lead.deliveryError,
            deliveryStatus: lead.deliveryStatus,
            status: lead.status,
            statusLabel: getLeadStatusLabel(lead.status),
            statusReason: lead.statusReason ?? "",
            responsible: campaign?.responsibleName ?? "",
            contact: lead.contact?.titles[0] ?? "",
          };
        }),
      [
        leads,
        campaignsById,
        editorLockState?.editorLocks,
        userNamesById,
        getLeadStatusLabel,
      ],
    );

    const buildItemLink = useMemo(
      () =>
        buildLeadLink && ((item: LeadsTableItem) => buildLeadLink(item.lead)),
      [buildLeadLink],
    );

    const onClickItem = useMemo(
      () => onClickLead && ((item: LeadsTableItem) => onClickLead(item.lead)),
      [onClickLead],
    );

    return (
      <typedTable.Table
        className={className}
        aria-label="Leads"
        columns={columns}
        items={items}
        sortDescriptor={sortDescriptor}
        setSortDescriptor={setSortDescriptor}
        buildItemLink={buildItemLink}
        onClickItem={onClickItem}
        data-lso-leads-table={true}
      >
        {children}
      </typedTable.Table>
    );
  },
);
